/**
 * Driving routes from an OSRM server — the driver portal's route line,
 * distance and duration.
 *
 * !! The default OSRM_ROUTE_URL (src/config/config.js) is OSRM's public DEMO
 * !! server: rate-limited (~1 request/second), no SLA, may block heavy users.
 * !! Production needs a self-hosted OSRM: set REACT_APP_OSRM_ROUTE_URL.
 *
 * Same contract as MapboxService.getRoute (and the same shaping, from
 * src/Utils/routeShape.js), so either can drive a route map: `{lat, lon}` in,
 * `{ distance (km, 1 decimal), duration (min), geometry }` out.
 *
 * Uses its own axios instance. API.js installs a request interceptor on the
 * DEFAULT axios instance that attaches the admin bearer token to every call;
 * that must never reach a third-party server. `axios.create()` does not copy
 * interceptors, and the request interceptor below also strips any
 * Authorization header an instance default could have merged in (the same
 * belt-and-braces PortalApi.js uses).
 */
import axios from 'axios';
import { OSRM_ROUTE_URL } from '../config/config';
import { ROUTE_PARAMS, toLonLatPath, toRouteResult } from '../Utils/routeShape';

// A driver is waiting on this screen; a hung demo server must not keep the
// card in limbo.
const OSRM_TIMEOUT_MS = 8000;

const osrmClient = axios.create({ timeout: OSRM_TIMEOUT_MS });

osrmClient.interceptors.request.use((config) => {
  const next = config;
  if (next.headers) delete next.headers.Authorization;
  return next;
});

const isPoint = (point) => Number.isFinite(Number(point?.lat))
  && Number.isFinite(Number(point?.lon));

/**
 * @param {{lat: number, lon: number}} from
 * @param {{lat: number, lon: number}} to
 * @param {Array<{lat: number, lon: number}>} waypoints - visited in array order
 * @returns {Promise<{distance: number, duration: number, geometry: Object}|null>}
 *   null when a point is unusable or OSRM finds no route; rejects on network or
 *   HTTP errors (OSRM answers "NoRoute" & co. with a 400).
 */
async function getRoute(from, to, waypoints = []) {
  const points = [from, ...waypoints, to];
  if (!points.every(isPoint)) return null;

  const { data } = await osrmClient.get(`${OSRM_ROUTE_URL}/${toLonLatPath(points)}`, {
    params: ROUTE_PARAMS,
  });

  const route = data?.code === 'Ok' ? data.routes?.[0] : null;
  if (!route?.geometry?.coordinates?.length) return null;

  return toRouteResult(route);
}

export default getRoute;
